import type { JSX } from 'react';

import { useSetTimeZone } from '../api/auth.ts';
import { Alert } from '../ui/Alert.tsx';
import { Field } from '../ui/Field.tsx';
import { inputClass } from '../ui/styles.ts';

/**
 * Every zone the browser knows, plus the saved one and UTC — which the browser's own list
 * leaves out, and which has to stay offerable as the default every account starts on.
 */
function timeZones(current: string): readonly string[] {
  return [...new Set(['UTC', current, ...Intl.supportedValuesOf('timeZone')])].toSorted((a, b) =>
    a.localeCompare(b),
  );
}

export interface TimeZoneFieldProps {
  readonly value: string;
}

/** The clock a rule's guards are read on; nothing else in the app depends on it (ADR-0019). */
export function TimeZoneField({ value }: TimeZoneFieldProps): JSX.Element {
  const setTimeZone = useSetTimeZone();

  return (
    <div className="flex flex-col gap-2 sm:max-w-sm">
      <Field label="Your time zone" hint="Rule conditions on days and times are read in it.">
        {(id) => (
          <select
            id={id}
            value={value}
            disabled={setTimeZone.isPending}
            onChange={(event) => {
              setTimeZone.mutate(event.target.value);
            }}
            className={inputClass}
          >
            {timeZones(value).map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
        )}
      </Field>
      {setTimeZone.isError ? <Alert>{setTimeZone.error.message}</Alert> : null}
    </div>
  );
}
